import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { LoginResponse } from "../types/common";
import { atlasWs } from "../api/ws";
import { AUTH_API_PREFIX } from "./authApiPaths";
import { useAuthStore } from "./authStore";

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }
  return res.json() as Promise<T>;
}

export function useAuth() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setFromLogin = useAuthStore((s) => s.setFromLogin);
  const clearSession = useAuthStore((s) => s.clearSession);

  const isAuthenticated = Boolean(accessToken);

  const login = useCallback(
    async (username: string, password: string): Promise<LoginResponse> =>
      postJson<LoginResponse>(`${AUTH_API_PREFIX}/login`, { username, password }),
    [],
  );

  const loginTwoFactor = useCallback(async (partialToken: string, totpCode: string): Promise<LoginResponse> => {
    return postJson<LoginResponse>(`${AUTH_API_PREFIX}/login/2fa`, {
      partial_token: partialToken,
      totp_code: totpCode,
    });
  }, []);

  const completeLogin = useCallback(
    (res: LoginResponse) => {
      setFromLogin(res);
      atlasWs.connect();
      navigate("/", { replace: true });
    },
    [navigate, setFromLogin],
  );

  const logout = useCallback(async () => {
    const { atlasWs } = await import("../api/ws");
    atlasWs.close();
    // Central logout: revokes the mavr-auth session via the rotated refresh
    // credential in the httpOnly cookie and clears that cookie. This is what
    // makes logout global — every other MAVR app sharing the issuer session
    // loses it too, not just this tab's access token.
    try {
      await fetch(`${AUTH_API_PREFIX}/oidc/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* ignore — local teardown continues */
    }
    const token = useAuthStore.getState().accessToken;
    const sessionId = useAuthStore.getState().user?.claims.session_id;
    try {
      if (token && sessionId) {
        // Legacy session revoke (password-mirror sessions); a no-op 401 for
        // central RS256 tokens.
        await fetch(`${AUTH_API_PREFIX}/revoke`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ session_id: sessionId }),
        });
      }
    } catch {
      /* ignore */
    }
    clearSession();
    navigate("/login", { replace: true });
  }, [clearSession, navigate]);

  return {
    user,
    isAuthenticated,
    login,
    loginTwoFactor,
    completeLogin,
    logout,
  };
}
